import { useSurfaceTheme } from '../../theme/surfaceTheme'

// One palette for every Dashboard chart, so the live price chart, the equity
// curve and the position sparklines read as a set. Red vs green alone is not
// colour-blind safe, so every chart also spells direction out as a signed
// number beside it.
//
// Dark: each colour clears 3:1 against the #141b22 card. Light: gains and
// losses are the light palette's (theme/light-theme.css), axis text is its
// muted grey (4.5:1+ on white) and the grid its border colour.
export const CHART_THEMES = {
  dark: {
    colors: { up: '#22c55e', down: '#f87171', flat: '#94a3b8' },
    grid: '#1f2a36',
    axisTick: { fill: '#8b98a5', fontSize: 11 },
    surface: '#141b22',
    cursor: '#64748b',
    tooltip: { background: '#141b22', border: '1px solid #1f2a36', color: '#f3f4f6' },
  },
  light: {
    colors: { up: '#15803d', down: '#b91c1c', flat: '#64748b' },
    grid: '#e2e7ef',
    axisTick: { fill: '#5b6577', fontSize: 11 },
    surface: '#ffffff',
    cursor: '#94a3b8',
    tooltip: { background: '#ffffff', border: '1px solid #e2e7ef', color: '#0f1a2b' },
  },
} as const

export type ChartTheme = (typeof CHART_THEMES)[keyof typeof CHART_THEMES]

/** The chart palette for the surface this chart is drawn on. */
export const useChartTheme = (): ChartTheme => CHART_THEMES[useSurfaceTheme()]

// The dark palette's values, for code outside a component.
export const CHART_COLORS = CHART_THEMES.dark.colors
export const GRID_COLOR = CHART_THEMES.dark.grid
export const AXIS_TICK = CHART_THEMES.dark.axisTick
export const CARD_SURFACE = CHART_THEMES.dark.surface
export const CURSOR_STROKE = CHART_THEMES.dark.cursor

export type ChartDirection = keyof typeof CHART_COLORS

export const directionOf = (value: number | null | undefined): ChartDirection =>
  value == null || value === 0 ? 'flat' : value > 0 ? 'up' : 'down'

/** 1, 2, 2.5 or 5 times a power of ten: the step a reader can count in. */
const niceStep = (rough: number): number => {
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)))
  const fraction = rough / magnitude
  return (fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10) * magnitude
}

/**
 * Round-number gridlines and the domain they span. Recharts spaces ticks
 * evenly across whatever domain it is given, so a padded min/max alone yields
 * labels like 79,995 / 78,186.
 */
export const niceScale = (values: number[], targetTicks = 4): { domain: [number, number]; ticks?: number[] } => {
  if (values.length === 0) return { domain: [0, 1] }
  let min = Math.min(...values)
  let max = Math.max(...values)
  if (min === max) {
    const pad = Math.abs(min) * 0.001 || 1
    min -= pad
    max += pad
  }
  const step = niceStep((max - min) / targetTicks)
  const low = Math.floor(min / step) * step
  const high = Math.ceil(max / step) * step
  const ticks: number[] = []
  for (let value = low; value <= high + step / 2; value += step) ticks.push(Number(value.toPrecision(12)))
  return { domain: [ticks[0], ticks[ticks.length - 1]], ticks }
}
